import type { Dispatch, SetStateAction } from "react"
import { createContext, useCallback, useContext, useRef, useState } from "react"

import type { ButtonBaseProps, SxProps } from "@mui/material"
import { Box, ButtonBase, Drawer, Stack, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { get, noop } from "lodash"

import type { FC, FCWC } from ".."
import {
  Button,
  InquiryModal,
  Link,
  ResponsiveImage,
  AutocompleteSearch,
  SvgIcon,
  UserCountrySelect,
  NavigationFavorites,
} from ".."
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import type { HeaderNavLink } from "./HeaderNav"

import "react-perfect-scrollbar/dist/css/styles.css"
import "./Header.css"

type HeaderNavMobileProps = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  links: HeaderNavLink[]
  showContactInfo?: boolean
}

const HeaderNavMobileContext = createContext<{
  activeLevel: [number, Dispatch<SetStateAction<number>>]
  onClose: () => void
  containerRef: React.RefObject<HTMLDivElement>
}>({ activeLevel: [0, noop], onClose: noop, containerRef: { current: null } })

/** A sub-component for the main nav section of the header. */
export const HeaderNavMobile: FC<HeaderNavMobileProps> = ({
  open,
  setOpen,
  links,
  showContactInfo,
}) => {
  const [activeLevel, setActiveLevel] = useState(0)
  const [openInquiryModal, setOpenInquiryModal] = useState(false)
  const [openSearchModal, setOpenSearchModal] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 3100,
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: (theme) => theme.spacing(1.5, 0, 0, 1.5),

            height: "auto",
            width: "100%",
            maxWidth: 378,
            top: 8,
            bottom: 8,
          },
        }}
        SlideProps={{
          timeout: 500,
        }}
      >
        <Box
          ref={containerRef}
          component="nav"
          position="relative"
          overflow="hidden"
          px={4}
          pb={5}
          height="100%"
          sx={{ isolation: "isolate" }}
        >
          <HeaderNavMobileContext.Provider
            value={{
              activeLevel: [activeLevel, setActiveLevel],
              onClose,
              containerRef,
            }}
          >
            <NavMobileList
              open={true}
              links={links}
              level={0}
              onOpenInquiryModal={() => setOpenInquiryModal(true)}
              onOpenSearchModal={() => {
                setOpenSearchModal(true)
                onClose()
              }}
              showContactInfo={showContactInfo}
            />
          </HeaderNavMobileContext.Provider>
        </Box>
      </Drawer>
      <InquiryModal open={openInquiryModal} onClose={() => setOpenInquiryModal(false)} />
      {openSearchModal && <AutocompleteSearch onClose={() => setOpenSearchModal(false)} />}
    </>
  )
}

type NavListProps = {
  open?: boolean
  links: HeaderNavLink[]
  images?: HeaderNavLink[]
  level?: number
  title?: string
  onOpenInquiryModal?: () => void
  onOpenSearchModal?: () => void
  onBack?: () => void
  showContactInfo?: boolean
}
const NavMobileList: FCWC<NavListProps> = ({
  open = false,
  links,
  images = [],
  level = 0,
  title = "",
  onBack = noop,
  onOpenInquiryModal = noop,
  onOpenSearchModal = noop,
  showContactInfo = true,
}) => {
  const siteMetadata = useContext(SiteMetadataContext)
  const excludedTitles = [
    "Africa",
    "Asia",
    "Australia & New Zealand",
    "Central America",
    "Europe",
    "Indian Ocean",
    "Middle East",
    "South America",
    "South Pacific",
    "US & Canada",
    "Arctic & Antarctica",
    "Popular Destinations",
  ]
  const isDestination = excludedTitles.includes(title)
  const hasPopularDestination =
    isDestination && links.some((item) => item.title === "Popular Destination")

  return (
    <>
      <Stack
        component="section"
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        right={0}
        px={4}
        pb={5}
        bgcolor={(theme) => theme.palette["neutral.100"].main}
        zIndex={level + 1}
        sx={{
          transform: `translateX(${open ? "0%" : "100%"})`,
          transition: (theme) => theme.transitions.create("transform"),
          overflow: "hidden",
        }}
      >
        <NavMobileListTop {...{ level, title, onBack }} />
        {excludedTitles.includes(title) && links.length > 0 && (
          <Typography
            variant="body.sm"
            component="div"
            sx={{ marginBottom: 4, marginTop: 4, columnSpan: 2 }}
          >
            Popular Countries
          </Typography>
        )}
        <Box
          //flex="1 1 fit-content"
          sx={[
            {
              "position": "static!important",
              "display": "flex",
              "flexDirection": "column",
              "overflowY": "auto",
              "height": level === 0 ? "90%" : "auto",
              "transition": (theme) => theme.transitions.create("all"),
              "zIndex": 2,
              "&::-webkit-scrollbar": {
                width: 2,
              },

              "&::-webkit-scrollbar-thumb": {
                background: (theme) => theme.palette["primary.800"].main,
              },

              "&::-webkit-scrollbar-thumb:hover": {
                background: (theme) => theme.palette["primary.900"].main,
              },
            },
            excludedTitles.includes(title) && {
              "position": "static!important",
              "display": "grid", // Use grid display
              "gridTemplateColumns": "repeat(2, 1fr)", // Two equal-width columns
              "overflowY": "auto",
              "transition": (theme) => theme.transitions.create("all"),
              "zIndex": 2,
              "mb": "60px",
              //"gridAutoRows": "minmax(50px, auto)",
              "&::-webkit-scrollbar": {
                width: 2,
              },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) => theme.palette["primary.800"].main,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: (theme) => theme.palette["primary.900"].main,
              },
            },
          ]}
        >
          {links.map((item, i) => (
            <>
              <NavMobileItem key={i} item={item} />
              {item.title === "All Destinations" && (
                <Box
                  display={"flex"}
                  position={"relative"}
                  height={"170px"}
                  width="100%"
                  alignItems={"end"}
                >
                  <Button
                    href={item.link.uri}
                    variant="tertiary"
                    sx={{
                      "backgroundColor": "#F05B2A",
                      "&:hover": {
                        backgroundColor: "#D04A22",
                      },
                      "position": "relative",
                      "height": 54,
                      "flexGrow": 1,
                    }}
                  >
                    Explore All Destinations
                  </Button>
                </Box>
              )}
            </>
          ))}
          {hasPopularDestination && (
            <Typography
              variant="body.sm"
              component="div"
              sx={{
                marginBottom: 4,
                marginTop: 12,
                gridColumn: "span 2",
              }}
            >
              Popular Destinations
            </Typography>
          )}
          {links.map(
            (item, i) =>
              item.title === "Popular Destination" &&
              item.children &&
              item.children.map((child, j) => (
                <>
                  <NavMobileItem key={j} item={child} />
                </>
              ))
          )}
        </Box>

        {isDestination && title !== "Popular Destinations" && (
          <Box display={"flex"} position={"absolute"} bottom={0} left={16} width={"92%"}>
            <Button href={images[0].link?.uri} variant="tertiary" sx={{ flexGrow: 1, mb: "20px" }}>
              Explore All {title}
            </Button>
          </Box>
        )}
        {images.length > 0 && title !== "Popular Destinations" && (
          <MobileNavImages images={images} title={title} exclude={excludedTitles} />
        )}

        {level === 0 && (
          <Stack rowGap={5} justifyContent={"flex-end"}>
            <Stack alignItems="stretch">
              <UserCountrySelect />
              <Link
                type="base"
                variant="link.xs"
                href="/wishlist"
                sx={(theme) => ({
                  borderTop: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 2,
                  px: { xs: 3 },
                  py: { xs: 5 },
                })}
              >
                <Box
                  sx={(theme) => ({
                    "display": "flex",
                    "borderRadius": 9999,
                    pr: { xs: 1.5 },
                    "transition": theme.transitions.create("background-color"),
                    "&:hover": {
                      background: theme.palette["tertiary.300"].main,
                    },
                  })}
                >
                  <NavigationFavorites size={12} />
                </Box>
                <span>My Wishlist</span>
              </Link>
              {showContactInfo && (
                <Link
                  type="base"
                  variant="link.xs"
                  href={siteMetadata.phoneUrl}
                  sx={(theme) => ({
                    borderTop: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    px: { xs: 3 },
                    py: { xs: 5 },
                  })}
                >
                  <SvgIcon
                    variant="phone"
                    size={14}
                    strokeMode
                    sx={(theme) => ({
                      fill: "none",
                      stroke: theme.palette["neutral.700"].main,
                      mr: 3,
                    })}
                  />
                  <span>{siteMetadata.phone}</span>
                </Link>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </>
  )
}

type ListTopProps = {
  level: number
  title: string
  onBack: () => void
}
const NavMobileListTop: FCWC<ListTopProps> = ({ level, title, onBack }) => {
  const { onClose } = useContext(HeaderNavMobileContext)

  return (
    <Stack
      py={5.5}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        ...(level > 0 && {
          borderBottom: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
        }),
      }}
    >
      {level > 0 && (
        <ButtonBase onClick={onBack}>
          <SvgIcon variant="arrow" size={16} />
        </ButtonBase>
      )}
      <Typography variant="link.sm" component="h2">
        {title}
      </Typography>
      <ButtonBase onClick={onClose}>
        <SvgIcon variant="x" size={16} />
      </ButtonBase>
    </Stack>
  )
}

type NavItemProps = {
  item: HeaderNavLink
  level?: number
}
const NavMobileItem: FCWC<NavItemProps> = ({ item, level = 0 }) => {
  const [open, setOpen] = useState(false)
  const {
    activeLevel: [activeLevel, setActiveLevel],
  } = useContext(HeaderNavMobileContext)

  const toggleItem = useCallback(() => {
    const newOpenState = !open
    setOpen(newOpenState)
    if (newOpenState) setActiveLevel((level) => level + 1)
    else setActiveLevel((level) => level - 1)
  }, [])

  const closeItem = useCallback(() => {
    setOpen(false)
    setActiveLevel((level) => level - 1)
  }, [])

  const attributes = get(item, "options.attributes.class", "")
  const { children = [], title } = item

  const isCategory = attributes.includes("category-title") ?? false

  const links = children.filter((child) => !child.image?.variants?.length) ?? []
  const images =
    children.filter((child) => child.image && child.image?.variants?.length > 0).slice(0, 1) ?? []
  return (
    <>
      {children.length > 0 ? (
        <>
          {isCategory ? (
            <>
              <Typography variant="body.sm" component="h2" px={4} py={4} sx={{ opacity: 0.7 }}>
                {title}
              </Typography>
              {children.map((item, i) => (
                <NavMobileItem key={i} item={item} level={level + 1} />
              ))}
            </>
          ) : (
            item.title !== "Popular Destination" && (
              <>
                <NavMobileLinkItem onClick={toggleItem} title={item.title} />
                <NavMobileList
                  open={open}
                  links={links}
                  images={images}
                  level={level + 1}
                  title={item.title}
                  onBack={closeItem}
                />
              </>
            )
          )}
        </>
      ) : (
        <>
          {item.title !== "All Destinations" && (
            <NavMobileLinkItem href={item.link.uri} title={item.title} />
          )}
        </>
      )}
    </>
  )
}

type LinkItemProps = ButtonBaseProps & {
  title: string
  href?: string
}
const NavMobileLinkItem: FC<LinkItemProps> = ({ title, href, onClick }) => {
  const theme = useTheme()
  const sxProps: SxProps<typeof theme> = {
    ...theme.typography["link.sm"],
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "space-between",
    "borderRadius": 1,
    "py": href ? 4 : 5.5,
    "transition": theme.transitions.create(["background-color", "color"]),
    "width": "100%",

    "&:hover, &:focus-visible": {
      bgcolor: theme.palette["secondary.200"].main,
      color: theme.palette["secondary.800"].main,
    },
  }

  if (href) {
    return (
      <Link type="base" href={href} sx={sxProps}>
        {title}
      </Link>
    )
  }

  return (
    <ButtonBase onClick={onClick} sx={sxProps}>
      {title}
      <SvgIcon variant="bullet" size={8} />
    </ButtonBase>
  )
}

/** A sub-component for an individual image in the dropdown content in the main nav of the header. */
const MobileNavImages: FC<{ images: HeaderNavLink[]; exclude: String[]; title: String }> = ({
  images,
  exclude,
  title,
}) => (
  <Stack flex="1 0 0" justifyContent="flex-end" rowGap={4} mt={5}>
    {images.map((item, i) => {
      // Grab the image variant that matches the style
      const image = {
        ...item.image!,
        variants:
          item.image?.variants
            .filter((variant) => variant.style === "thumbnail")
            .map((item) => ({ ...item, style: "default" })) ?? [],
      }
      return (
        !exclude.includes(title) && (
          <Box key={i} borderRadius={1.5} overflow="hidden">
            <Box
              position="relative"
              width="100%"
              height="100%"
              sx={{
                "minHeight": 190,
                "isolation": "isolate",
                "&:hover, &:focus-within": {
                  img: {
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              <Link type="overlay" href={item.link.uri} title={item.title} zIndex={3} />
              <Box
                position="absolute"
                width="100%"
                height="100%"
                sx={{
                  background: (theme) =>
                    `linear-gradient(180deg, ${alpha(
                      theme.palette["neutral.900"].main,
                      0
                    )} 0%, ${alpha(theme.palette["neutral.900"].main, 0.4)} 100%)`,
                  pointerEvents: "none",
                }}
                zIndex={1}
              />
              <ResponsiveImage
                image={image}
                sx={{
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  objectFit: "cover",
                  transition: (theme) =>
                    theme.transitions.create("transform", {
                      duration: theme.transitions.duration.standard * 1.5,
                    }),
                }}
              />
              <Box
                position="absolute"
                bottom="0"
                width="100%"
                p={5}
                zIndex={2}
                sx={{ pointerEvents: "none" }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  color={(theme) => theme.palette["neutral.100"].main}
                >
                  <Box>
                    {item.description && (
                      <Typography variant="body.xs" component="div">
                        {item.description}
                      </Typography>
                    )}
                    <Typography variant="body.lg" component="div">
                      {item.title}
                    </Typography>
                  </Box>
                  <Box alignSelf="flex-end">
                    <SvgIcon variant="arrowOutward" size={14} />
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        )
      )
    })}
  </Stack>
)
